import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { publicApi } from "../lib/api";
import { ImageOff } from "lucide-react";

const PLACEHOLDER = [
  {
    id: "p1",
    image_base64:
      "https://images.unsplash.com/photo-1774977737078-7ccc2ac697e6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwzfHxob3VzZSUyMGV4dGVyaW9yJTIwcGFpbnRpbmd8ZW58MHx8fHwxNzg1MjQ1NDUxfDA&ixlib=rb-4.1.0&q=85",
    title: "Exterior refresh",
    category: "Exterior",
  },
  {
    id: "p2",
    image_base64:
      "https://images.unsplash.com/photo-1615873968403-89e068629265",
    title: "Interior accent wall",
    category: "Interior",
  },
  {
    id: "p3",
    image_base64:
      "https://images.unsplash.com/photo-1647579350449-37960d792a05?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwyfHxob3VzZSUyMGV4dGVyaW9yJTIwcGFpbnRpbmd8ZW58MHx8fHwxNzg1MjQ1NDUxfDA&ixlib=rb-4.1.0&q=85",
    title: "Modern facade",
    category: "Exterior",
  },
  {
    id: "p4",
    image_base64:
      "https://images.unsplash.com/photo-1652829069834-2c05031199c5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwyfHxwYWludGVyJTIwdXNpbmclMjByb2xsZXJ8ZW58MHx8fHwxNzg1MjQ1NDUyfDA&ixlib=rb-4.1.0&q=85",
    title: "Craft in progress",
    category: "Other",
  },
  {
    id: "p5",
    image_base64:
      "https://images.unsplash.com/photo-1613490493576-7fde63acd811?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBsdXh1cnklMjBob21lfGVufDB8fHx8MTc4NTI0NTQ1Mnww&ixlib=rb-4.1.0&q=85",
    title: "Luxury build",
    category: "Exterior",
  },
];

export default function Gallery() {
  const [photos, setPhotos] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [filter, setFilter] = useState("All");

  useEffect(() => {
    publicApi
      .get("/photos")
      .then((r) => {
        const data = r.data && r.data.length ? r.data : PLACEHOLDER;
        // Sort: featured first, then by created_at desc (backend already returns newest-first)
        data.sort((a, b) => Number(!!b.featured) - Number(!!a.featured));
        setPhotos(data);
      })
      .catch(() => setPhotos(PLACEHOLDER))
      .finally(() => setLoaded(true));
  }, []);

  const items = loaded ? photos : [];
  const filtered = filter === "All" ? items : items.filter((p) => p.category === filter);
  const categoriesPresent = Array.from(new Set(items.map((p) => p.category)));
  const showFilter = items.length > 4 && categoriesPresent.length > 1;
  const displayCount = filter === "All" ? 12 : 24;

  return (
    <section
      id="gallery"
      data-testid="gallery-section"
      className="relative py-24 md:py-40 border-t border-brand-border overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
          <div>
            <div className="text-[11px] tracking-[0.3em] uppercase text-brand-muted mb-4 flex items-center gap-3">
              <span className="h-px w-8 bg-brand-blue" />
              Selected Work
            </div>
            <h2 className="font-display uppercase text-6xl md:text-8xl leading-[0.88]">
              The <span className="text-brand-blue">Work</span> <br />
              Speaks First.
            </h2>
          </div>
          <p className="text-brand-muted max-w-sm text-base">
            A rotating selection of homes, businesses, and interiors we&apos;ve
            transformed across Central Alabama.
          </p>
        </div>

        {showFilter && (
          <div className="flex flex-wrap gap-2 mb-10" data-testid="gallery-filters">
            {["All", ...categoriesPresent].map((cat) => (
              <button
                key={cat}
                onClick={() => setFilter(cat)}
                data-testid={`gallery-filter-${cat.toLowerCase()}`}
                className={`px-5 py-2.5 text-[11px] tracking-[0.3em] uppercase transition-colors border ${
                  filter === cat
                    ? "bg-brand-blue border-brand-blue text-white"
                    : "border-brand-border text-brand-muted hover:text-white hover:border-white"
                }`}
              >
                {cat}
                <span className="ml-2 text-[10px] opacity-60">
                  {cat === "All" ? items.length : items.filter((p) => p.category === cat).length}
                </span>
              </button>
            ))}
          </div>
        )}

        {items.length === 0 ? (
          <div className="text-center text-brand-muted py-24 flex flex-col items-center gap-3">
            <ImageOff className="h-8 w-8" />
            No photos yet. Check back soon.
          </div>
        ) : (
          <div className="grid grid-cols-12 auto-rows-[220px] md:auto-rows-[300px] gap-4">
            {filtered.slice(0, displayCount).map((p, i) => {
              const layouts = [
                "col-span-12 md:col-span-8 row-span-2",
                "col-span-12 md:col-span-4",
                "col-span-6 md:col-span-4",
                "col-span-6 md:col-span-4 row-span-2",
                "col-span-12 md:col-span-4",
                "col-span-12 md:col-span-8",
                "col-span-6 md:col-span-4 row-span-2",
                "col-span-12 md:col-span-8",
                "col-span-6 md:col-span-4",
                "col-span-12 md:col-span-8",
                "col-span-6 md:col-span-4",
                "col-span-6 md:col-span-4",
              ];
              return (
                <motion.div
                  key={p.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{ duration: 0.8, delay: i * 0.05, ease: [0.6, 0.05, 0.15, 1] }}
                  className={`relative group overflow-hidden bg-brand-surface border border-brand-border ${layouts[i % layouts.length]}`}
                  data-testid={`gallery-item-${p.id}`}
                >
                  <img
                    src={p.image_base64}
                    alt={p.title || "Painting & construction project in Montgomery, AL"}
                    loading="lazy"
                    decoding="async"
                    style={p.rotation ? { transform: `rotate(${p.rotation}deg) scale(1.6)` } : undefined}
                    className="absolute inset-0 h-full w-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700 scale-100 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-brand-bg via-brand-bg/20 to-transparent opacity-90 group-hover:opacity-60 transition-opacity" />
                  {p.stage && (
                    <span
                      className={`absolute top-4 left-4 text-[10px] tracking-[0.3em] uppercase px-3 py-1 font-semibold ${
                        p.stage === "after"
                          ? "bg-brand-blue text-white"
                          : "bg-white text-brand-bg"
                      }`}
                    >
                      {p.stage}
                    </span>
                  )}
                  <div className="absolute bottom-0 left-0 p-6 flex flex-col gap-2">
                    <span className="text-[10px] tracking-[0.3em] uppercase text-brand-blue">
                      {p.category}
                    </span>
                    <span className="font-display uppercase text-2xl md:text-3xl leading-none">
                      {p.title || "Project"}
                    </span>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
